import { ActionIcon, Flex, Group, Modal, Stack, TextInput } from '@mantine/core';
import type { DataEntryColumnData, DataEntryComponent } from '../../../../api/pages-client/pages-client.type';
import { DataEntryDataType } from '../../../../api/pages-client/pages-client.type';
import i18n from '../../../../base-dictionary';
import { inkLight, redBase, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Select } from '../../../../ui-library/select/select';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { DashboardModalStyled } from '../dashboard-modal';
import { useDataEntryModal } from './data-entry-widget-modal.hooks';
import { DATA_ENTRY_DATA_TYPE_OPTIONS } from './data-entry-widget.types';

interface DataEntryWidgetModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (widget: DataEntryComponent) => void;
  initialData?: DataEntryComponent;
  isEditing?: boolean;
}

export const DataEntryWidgetModal: React.FC<DataEntryWidgetModalProps> = ({
  opened,
  onClose,
  onSave,
  initialData,
  isEditing,
}) => {
  const {
    formData,
    errors,
    isSubmitting,
    confirmationModal,
    widgetString,
    isFormValid,
    handleClose,
    handleSubmit,
    handleNameChange,
    handleDescriptionChange,
    handleColumnNameChange,
    handleColumnDataTypeChange,
    removeColumn,
    handleConfirmAction,
    handleCancelConfirmation,
    addColumn,
  } = useDataEntryModal(opened, initialData, isEditing, onSave, onClose);

  const renderColumnData = (columnData: DataEntryColumnData, index: number) => {
    const columnErrors = errors.column_data?.[index];

    return (
      <Stack key={index} gap="xs">
        <Group justify="space-between" align="center">
          <TextInput
            placeholder="Enter Column Name"
            value={columnData.name}
            w="250px"
            error={columnErrors?.name}
            onChange={(e) => handleColumnNameChange(index, e.target.value)}
          />
          <Group align="center">
            <Select
              placeholder="Select Column Data Type"
              radius="xl"
              value={columnData.data_type}
              options={DATA_ENTRY_DATA_TYPE_OPTIONS}
              onChange={(value) => handleColumnDataTypeChange(index, value as DataEntryDataType)}
              style={{ width: '250px' }}
            />
            <Tooltip label="Delete Column" variant="black" zIndex={1000}>
              <ActionIcon variant="transparent" onClick={() => removeColumn(index)}>
                <Icon name="delete" size={20} color={secondaryBase} style={{ cursor: 'pointer' }} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Stack>
    );
  };

  return (
    <>
      <DashboardModalStyled
        opened={opened}
        onClose={handleClose}
        title={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Text size="large" weight="bold">
              {`${isEditing ? 'Edit' : 'Add'} ${i18n.t('common.dashboard.data_entry')} ${widgetString}`}
            </Text>
            <Tooltip
              label={
                <Text>
                  A Data Entry Widget allows you to enter data manually into Bloomfilter. This data is entered into a
                  table. Select the columns and their data types you want to display in the table.
                </Text>
              }
              variant="black"
              multiline
              style={{ maxWidth: '400px' }}
              zIndex={1000}
            >
              <Icon name="info" size={20} color={secondaryBase} />
            </Tooltip>
          </div>
        }
        size="lg"
        padding="lg"
        centered
      >
        <form onSubmit={handleSubmit}>
          <Stack>
            <TextInput
              label={`${widgetString} Name`}
              placeholder={`Enter ${widgetString.toLowerCase()} name`}
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              error={errors.name}
              required
            />

            <TextInput
              label={`${widgetString} Description`}
              placeholder={`Enter ${widgetString.toLowerCase()} description`}
              value={formData.description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              error={errors.description}
            />
          </Stack>

          <Stack mt="md">
            <Group justify="space-between">
              <Tooltip
                label="Add a column to the table. Column names must be unique. If you change the name of a prexisting column all its data will be lost."
                variant="black"
                multiline
                style={{ maxWidth: '300px' }}
                zIndex={1000}
              >
                <Group gap={5}>
                  <Text size="medium" weight="bold">
                    Columns
                  </Text>

                  <Icon name="info" size={16} color={secondaryBase} />
                </Group>
              </Tooltip>
              <Button
                variant="secondary"
                radius="xl"
                size="sm"
                onClick={addColumn}
                leftSection={<Icon name="add_circle" size={16} color="white" />}
              >
                Add Column
              </Button>
            </Group>
            <Stack gap={2}>
              {formData.column_data.length === 0 ? (
                <Text size="small" style={{ color: 'var(--mantine-color-dimmed)' }}>
                  {`Add at least one column to create the ${widgetString.toLowerCase()}`}
                </Text>
              ) : (
                <Group justify="space-between" mb="xs">
                  <Text size="small" weight="medium" style={{ flex: 1 }}>
                    Column Name
                  </Text>
                  <Text size="small" weight="medium" style={{ flex: 1 }}>
                    Data Type
                  </Text>
                  <div style={{ width: '0px' }}></div>
                </Group>
              )}
              <Stack>{formData.column_data.map((columnData, index) => renderColumnData(columnData, index))}</Stack>
            </Stack>
          </Stack>

          <Divider style={{ marginTop: '32px' }} />
          <Flex justify="flex-end" mt="xl" gap="md">
            <Button variant="outline" radius="xl" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              radius="xl"
              variant="secondary"
              loading={isSubmitting}
              rightSection={<Icon name="check_circle" size={16} color="white" />}
            >
              {isEditing ? 'Save Changes' : `Create ${widgetString}`}
            </Button>
          </Flex>
        </form>
      </DashboardModalStyled>

      {/* Confirmation Modal */}
      <Modal
        opened={confirmationModal.isOpen}
        onClose={handleCancelConfirmation}
        centered
        radius="lg"
        size="md"
        padding="32px"
        withCloseButton={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Icon
            name="close"
            size={24}
            color={skyDark}
            style={{ position: 'absolute', top: 16, right: 16, cursor: 'pointer' }}
            onClick={handleCancelConfirmation}
          />
          <Text size="large" weight="bold" style={{ marginBottom: 16 }}>
            {confirmationModal.type === 'delete' ? 'Delete Column' : 'Rename Column'}
          </Text>
          <Text style={{ marginBottom: 8 }}>
            {confirmationModal.type === 'delete'
              ? `Are you sure you want to delete the column "${confirmationModal.columnName}"?`
              : `Are you sure you want to rename the column "${confirmationModal.columnName}"?`}
          </Text>
          <Text color={redBase} weight="medium" style={{ marginBottom: 24 }}>
            This action will permanently delete all data in this column and cannot be undone.
          </Text>
          <Group justify="flex-end" gap="md">
            <Button
              variant="outline"
              onClick={handleCancelConfirmation}
              radius="xl"
              leftSection={<Icon name="cancel" size={16} color={inkLight} />}
            >
              <Text weight="medium" style={{ color: inkLight }}>
                Cancel
              </Text>
            </Button>
            <Button
              onClick={handleConfirmAction}
              radius="xl"
              style={{ backgroundColor: redBase }}
              leftSection={
                <Icon name={confirmationModal.type === 'delete' ? 'delete' : 'edit'} size={16} color="white" />
              }
            >
              <Text weight="medium" style={{ color: 'white' }}>
                {confirmationModal.type === 'delete' ? 'Delete Column' : 'Rename Column'}
              </Text>
            </Button>
          </Group>
        </div>
      </Modal>
    </>
  );
};
